import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdSpace from '@/components/AdSpace';
import SubscribeBox from '@/components/SubscribeBox';
import HotTopics from '@/components/HotTopics';
import TornPaperDivider from '@/components/TornPaperDivider';
import SEO from '@/components/SEO';
import { getArticles, getBiggestStoryToday, incrementPageViews } from '@/lib/api';
import { Loader2, Play, Eye, Newspaper } from 'lucide-react';
import { format } from 'date-fns';
import { CategoryPreviewRow } from '@/components/home/CategoryPreviewRow';

const HOMEPAGE_ROWS = [
  {
    key: 'Check On Yo Peoples News Feed',
    title: 'Check On Yo Peoples News Feed',
    viewAllPath: '/allstories',
    testId: 'homepage-feed-row',
  },
  {
    key: 'Cincy Worldstar',
    title: 'Cincy Worldstar',
    viewAllPath: '/worldstar',
    testId: 'homepage-worldstar-row',
  },
  {
    key: 'Check Your Claims',
    title: 'Check Your Claims',
    viewAllPath: '/check-your-claims',
    testId: 'homepage-claims-row',
  },
];

const [feedRowConfig, worldstarRowConfig, claimsRowConfig] = HOMEPAGE_ROWS;

const isVideoArticle = (article) => article?.category === 'Cincy Worldstar' || article?.post_type === 'video';

/* ── Latest Story Hero ── */
const BiggestStoryHero = ({ article }) => {
  if (!article) return null;
  const url = `/article/${article.slug || article.id}`;
  const formattedDate = article.published_at ? format(new Date(article.published_at), 'MMM d, yyyy') : '';
  const isVideo = isVideoArticle(article);

  return (
    <div className="relative w-full bg-black overflow-hidden border-b border-[#1a1a1a]" data-testid="biggest-story-hero">
      <Link
        to={url}
        className="relative z-10 flex flex-col w-full animate-fade-in-up group"
        data-testid="biggest-story-hero-link"
      >
        <div className="relative w-full h-[40vh] sm:h-[52vh] overflow-hidden shadow-[0_0_60px_rgba(202,138,4,0.18)] bg-black">
          {article.featured_image ? (
            <>
              <img
                src={article.featured_image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover blur-2xl brightness-[0.45] saturate-150"
              />
              <img
                src={article.featured_image}
                alt={article.title}
                className="relative z-[1] w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
              />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1a1200] to-black flex items-center justify-center">
              <span className="text-yellow-600 text-5xl font-black">C</span>
            </div>
          )}

          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300" />

          <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5" data-testid="biggest-story-hero-type-badge">
            {isVideo ? <Play size={10} fill="currentColor" className="text-yellow-500" /> : <Newspaper size={10} className="text-yellow-500" />}
            {isVideo ? 'Video' : 'Story'}
          </span>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-yellow-600/50 blur-xl scale-125 group-hover:scale-[1.8] transition-transform duration-500" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                {isVideo ? <Play size={26} fill="black" className="text-black ml-1" /> : <Newspaper size={26} className="text-black" />}
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 bg-black px-4 sm:px-10 py-6 sm:py-8 max-w-4xl mx-auto w-full">
          <span className="inline-flex items-center gap-2 bg-yellow-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-2.5 py-1 mb-4" data-testid="biggest-story-hero-kicker">
            <Newspaper size={12} />
            Latest Story
          </span>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
            {article.title}
          </h2>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-5">
            <Eye size={13} className="text-green-500" />
            <span className="text-green-500 font-medium">{(article.views || 0).toLocaleString()} views</span>
            <span>·</span>
            <span>{formattedDate}</span>
          </div>
          <p className="text-sm sm:text-base text-gray-300 max-w-2xl mb-5" data-testid="biggest-story-hero-description">
            The newest published story across the site appears here first.
          </p>
          <span className="inline-flex items-center gap-2 bg-yellow-600 text-white text-sm font-bold px-6 py-3 group-hover:bg-yellow-500 transition-colors duration-300">
            <Play size={14} fill="currentColor" /> {isVideo ? 'Watch Now' : 'Read Now'}
          </span>
        </div>
      </Link>
    </div>
  );
};

/* ── Main Page ── */
const HomePage = () => {
  const [heroStory, setHeroStory] = useState(null);
  const [categoryRows, setCategoryRows] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const latestStoryResponse = await getArticles({ limit: 1 });
        const rowResponses = await Promise.allSettled(
          HOMEPAGE_ROWS.map((row) => getBiggestStoryToday({
            category: row.key,
            fallback_mode: 'latest_day_best',
            limit: 4,
          }))
        );

        const nextRows = HOMEPAGE_ROWS.reduce((acc, row, index) => {
          const result = rowResponses[index];
          if (result.status !== 'fulfilled') {
            acc[row.key] = [];
            return acc;
          }
          acc[row.key] = result.value?.data?.articles || (result.value?.data?.article ? [result.value.data.article] : []);
          return acc;
        }, {});
        setCategoryRows(nextRows);
        setHeroStory(latestStoryResponse.data?.[0] || null);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
    incrementPageViews().catch(() => {});
  }, []);

  const fallbackStory = (categoryRows['Check On Yo Peoples News Feed'] || [])[0] || (categoryRows['Check Your Claims'] || [])[0] || null;
  const heroArticle = heroStory || fallbackStory;
  const rowArticles = (category) => (categoryRows[category] || [])
    .filter((article) => article.id !== heroArticle?.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-black page-enter pb-24" data-testid="home-page">
      <SEO
        title="Home Page"
        description="Check On Yo Peoples TV - Cincinnati's Source for News and Entertainment"
      />
      <Header />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      ) : (
        <>
          <BiggestStoryHero article={heroArticle} />

          <TornPaperDivider fillColor="#050505" />

          <div className="bg-[#050505]">
            <div className="px-4 sm:px-10 py-4">
              <AdSpace type="header" />
            </div>

            <CategoryPreviewRow
              title={feedRowConfig.title}
              viewAllPath={feedRowConfig.viewAllPath}
              testId={feedRowConfig.testId}
              articles={rowArticles('Check On Yo Peoples News Feed')}
            />
            <CategoryPreviewRow
              title={worldstarRowConfig.title}
              viewAllPath={worldstarRowConfig.viewAllPath}
              testId={worldstarRowConfig.testId}
              articles={rowArticles('Cincy Worldstar')}
            />
            <CategoryPreviewRow
              title={claimsRowConfig.title}
              viewAllPath={claimsRowConfig.viewAllPath}
              testId={claimsRowConfig.testId}
              articles={rowArticles('Check Your Claims')}
            />

            <div className="px-4 sm:px-10">
              <AdSpace type="footer" className="mt-4" />
              <HotTopics />
              <HotTopics period="last_week" heading="Last Week's Hottest" />
              <div className="mt-6 mb-8">
                <SubscribeBox />
              </div>
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
